import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { fetchDashboardStats, calculateAverageReceipt } from '@/services/analyticsService';
import { CourierDetailsDialog } from '@/components/couriers/CourierDetailsDialog';
import { Courier } from '@/services/types';
import { supabase } from '@/integrations/supabase/client';
import { 
  BadgeDollarSign, 
  Bike, 
  Users,
  TrendingUp,
  TrendingDown,
  CalendarClock,
  CircleDollarSign,
  LineChart,
  Map,
  ArrowRight,
  AlertTriangle,
  Loader2,
  Receipt,
  BarChart,
  RefreshCw
} from 'lucide-react';
import { KeyMetricsPanel } from '@/components/analytics/MetricsPanel';
import { ExpiringRentalsPanel } from '@/components/rentals/ExpiringRentalsPanel';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { toast } from '@/components/ui/sonner';
import { ExternalMapWidget } from '@/components/admin/ExternalMapWidget';
import { Button } from '@/components/ui/button';
import { useAttentionRequiredRentals } from '@/hooks/useAttentionRequiredRentals';

const DashboardStatsCard: React.FC<{
  title: string;
  value: string;
  icon: React.ReactNode;
  description?: string;
  trend?: number;
  className?: string;
  loading?: boolean;
}> = ({ title, value, icon, description, trend, className, loading = false }) => {
  const isMobile = useIsMobile();
  
  return (
    <Card className={`dashboard-stat-card hover-scale ${className}`}>
      <CardHeader className="flex flex-row items-center justify-between pb-2 px-4 pt-4">
        <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        <div className="rounded-full bg-primary/10 p-2 text-primary">
          {icon}
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4 pt-0">
        {loading ? (
          <div className="flex items-center space-x-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm text-muted-foreground">Загрузка...</span>
          </div>
        ) : (
          <>
            <div className="text-xl sm:text-2xl md:text-3xl font-bold">{value}</div>
            {description && (
              <p className="text-xs text-muted-foreground mt-1">{description}</p>
            )}
            {trend !== undefined && (
              <div className={`text-xs flex items-center mt-1 ${trend >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {trend >= 0 ? (
                  <TrendingUp className="h-3 w-3 mr-1" />
                ) : (
                  <TrendingDown className="h-3 w-3 mr-1" />
                )}
                <span>{trend >= 0 ? '+' : ''}{trend}%</span>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Фиксированные тарифные ставки
const TARIFF_PRICES = {
  weekly: 4500,
  biweekly: 8000, 
  monthly: 14000,
  rentToOwn: 20000 // Аванс за аренду с выкупом
};

const Dashboard: React.FC = () => {
  const [dashboardStats, setDashboardStats] = useState<any>({
    totalPayments: 0,
    currentMonthRevenue: 0, // Доход за текущий месяц
    totalRentalValue: 0,
    activeRentals: 0,
    activeCouriers: 0,
    avgRentalAmount: 0,
    expiringRentals: [],
    recentPayments: [],
    activeBikes: 0,
    totalBikes: 0,
    revenueGrowth: 0 // Прирост дохода
  });
  const [loading, setLoading] = useState(true);
  const [selectedCourier, setSelectedCourier] = useState<Courier | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Добавляем использование хука для управления данными арендаторов, требующих внимания
  const { 
    rentals: attentionRentals,
    refetch: refetchAttentionRentals,
    isLoading: loadingAttentionRentals,
    lastUpdated: lastAttentionUpdate
  } = useAttentionRequiredRentals({ 
    days: 2, // Изменяем с 7 на 2 дня
    enabled: true 
  });

  // Получение данных для дашборда при загрузке компонента
  useEffect(() => {
    const loadStats = async () => {
      setLoading(true);
      try {
        console.log('Начинаем загрузку статистики дашборда... isMobile:', isMobile);
        
        // Загружаем статистику из стандартной функции
        const stats = await fetchDashboardStats();
        
        console.log('Загружены данные дашборда:', stats);
        
        setDashboardStats(stats);
      } catch (error) {
        console.error('Ошибка при загрузке статистики дашборда:', error);
        toast.error('Не удалось загрузить данные статистики');
      } finally {
        setLoading(false);
      }
    };
    
    loadStats();

    // Обновляем данные каждые 5 минут
    const intervalId = setInterval(loadStats, 5 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, [isMobile]);

  const handleRentalClick = async (rentalId: string) => {
    try {
      const { data: rental, error: rentalError } = await supabase
        .from('rentals')
        .select('courier_id')
        .eq('id', rentalId)
        .single();

      if (rentalError) throw rentalError;

      if (rental?.courier_id) {
        const { data: courier, error: courierError } = await supabase
          .from('couriers')
          .select('*')
          .eq('id', rental.courier_id)
          .single();

        if (courierError) throw courierError;

        if (courier) {
          // Преобразуем данные из Supabase в формат, соответствующий интерфейсу Courier
          const courierData: Courier = {
            id: courier.id,
            name: courier.name,
            email: courier.email,
            phone: courier.phone,
            status: courier.status as 'active' | 'inactive',
            totalRentals: courier.totalrentals || 0,
            registeredAt: courier.created_at || new Date().toISOString(),
            registrationDate: courier.created_at || new Date().toISOString(), // Добавляем registrationDate
            avatar: courier.avatar,
            deal_number: courier.deal_number,
            tariff: courier.tariff,
            amo_deal_id: courier.amo_deal_id,
            amo_contact_id: courier.amo_contact_id,
            telegram_id: courier.telegram_id,
            avito_id: courier.avito_id,
            vk_id: courier.vk_id,
            passport_number: courier.passport_number,
            registration_address: courier.registration_address,
            rental_start_ts: courier.rental_start_ts,
            rental_end_ts: courier.rental_end_ts,
            rental_end_human: courier.rental_end_human,
            last_payment_date: courier.last_payment_date,
            comments: courier.comments,
            has_paid: courier.has_paid
          };
          
          setSelectedCourier(courierData);
          setShowDetailsDialog(true);
          return;
        }
      }

      navigate(`/admin/rentals?highlight=${rentalId}`);
    } catch (error) {
      console.error('Ошибка при получении данных:', error);
      navigate(`/admin/rentals?highlight=${rentalId}`);
    }
  };

  const handleToggleStatus = async (courier: Courier) => {
    try {
      const { data, error } = await supabase
        .from('couriers')
        .update({ status: courier.status === 'active' ? 'inactive' : 'active' })
        .eq('id', courier.id)
        .select()
        .single();

      if (error) throw error;

      if (data && selectedCourier?.id === courier.id) {
        // Преобразуем данные из Supabase в формат, соответствующий интерфейсу Courier
        const updatedCourier: Courier = {
          id: data.id,
          name: data.name,
          email: data.email,
          phone: data.phone,
          status: data.status as 'active' | 'inactive',
          totalRentals: data.totalrentals || 0,
          registeredAt: data.created_at || new Date().toISOString(),
          registrationDate: data.created_at || new Date().toISOString(), // Добавляем registrationDate
          avatar: data.avatar,
          deal_number: data.deal_number,
          tariff: data.tariff,
          amo_deal_id: data.amo_deal_id,
          amo_contact_id: data.amo_contact_id,
          telegram_id: data.telegram_id,
          avito_id: data.avito_id,
          vk_id: data.vk_id,
          passport_number: data.passport_number,
          registration_address: data.registration_address,
          rental_start_ts: data.rental_start_ts,
          rental_end_ts: data.rental_end_ts,
          rental_end_human: data.rental_end_human,
          last_payment_date: data.last_payment_date,
          comments: data.comments,
          has_paid: data.has_paid
        };
        
        setSelectedCourier(updatedCourier);
      }
    } catch (error) {
      console.error('Ошибка при обновлении статуса:', error);
    }
  };

  const getTrendFor = (key: string): number => {
    const trends: Record<string, number> = {
      totalPayments: 12.5,
      currentMonthRevenue: dashboardStats.revenueGrowth || 0, // Используем реальный прирост
      activeRentals: 5.2,
      activeCouriers: 15.3,
      avgRentalAmount: 8.1
    };
    return trends[key] || 0;
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatLastUpdated = (date: Date | null): string => {
    if (!date) return 'Нет данных';
    
    return date.toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      <div className="bg-card/40 backdrop-blur-sm p-4 sm:p-6 rounded-lg shadow-sm border border-border/40 animate-fade-in">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-1">Рабочий стол</h1>
        <p className="text-muted-foreground">
          Управление арендой велосипедов для курьеров
        </p>
      </div>
      
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="bg-card/50 backdrop-blur-sm p-1 border border-border/40 w-full sm:w-auto">
          <TabsTrigger value="overview" className="data-[state=active]:bg-card/90">
            <LineChart className="h-4 w-4 mr-2 hidden sm:inline" />
            Обзор
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="space-y-6 md:space-y-8">
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            <DashboardStatsCard
              title="Общая выручка"
              value={formatCurrency(dashboardStats.totalPayments)}
              trend={getTrendFor('totalPayments')}
              icon={<BadgeDollarSign className="h-4 w-4 sm:h-5 sm:w-5 text-tuktuk-teal" />}
              loading={loading}
              description="Вся выручка за все время"
            />
            
            <DashboardStatsCard
              title="Оплачено Аренд"
              value={dashboardStats.activeRentals.toString()}
              trend={getTrendFor('activeRentals')}
              icon={<Bike className="h-4 w-4 sm:h-5 sm:w-5 text-tuktuk-teal" />}
              loading={loading}
            />
            
            <DashboardStatsCard
              title="Оплатило Курьеров"
              value={dashboardStats.activeCouriers.toString()}
              trend={getTrendFor('activeCouriers')}
              icon={<Users className="h-4 w-4 sm:h-5 sm:w-5 text-tuktuk-teal" />}
              loading={loading}
            />
            
            <DashboardStatsCard
              title="Средний чек"
              value={formatCurrency(dashboardStats.avgRentalAmount)}
              trend={getTrendFor('avgRentalAmount')}
              icon={<Receipt className="h-4 w-4 sm:h-5 sm:w-5 text-tuktuk-teal" />}
              loading={loading}
              description="Средний размер платежа"
            />
          </div>
          
          {/* Секция "Требуют внимания" */}
          <Card className="border-border/40 bg-card/20 backdrop-blur-sm">
            <CardContent className="p-4">
              <ExpiringRentalsPanel onRentalClick={handleRentalClick} days={2} />
            </CardContent>
          </Card>
          
          {/* Карта велосипедов с отступом сверху и снизу */}
          <div className="mt-8 mb-10">
            <ExternalMapWidget url="https://tuk-tuk.rent/bikes-map" />
          </div>
          
          {/* Секция метрик под картой с улучшенными отступами */}
          <div className="grid gap-6 grid-cols-1 lg:grid-cols-2 pt-4">
            {/* Секция "Клиенты и выручка" */}
            <Card className="border-border/40 bg-card/20 backdrop-blur-sm h-full">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center">
                  <Users className="h-5 w-5 mr-2 text-tuktuk-teal" />
                  Клиенты и выручка
                </CardTitle>
              </CardHeader>
              <CardContent>
                <KeyMetricsPanel />
              </CardContent>
            </Card>
            
            {/* Секция "Статистика платежей" */}
            <Card className="border-border/40 bg-card/20 backdrop-blur-sm h-full">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center">
                  <BarChart className="h-5 w-5 mr-2 text-tuktuk-teal" />
                  Статистика платежей
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium`}>Этот месяц</span>
                    <span className={`${isMobile ? 'text-base' : 'text-lg'} font-semibold`}>
                      {loading ? (
                        <div className="h-5 bg-muted rounded animate-pulse w-16 inline-block"></div>
                      ) : (
                        formatCurrency(dashboardStats.currentMonthRevenue || 0)
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium`}>Прошлый месяц</span>
                    <span className={`${isMobile ? 'text-base' : 'text-lg'} font-semibold`}>
                      {loading ? (
                        <div className="h-5 bg-muted rounded animate-pulse w-16 inline-block"></div>
                      ) : (
                        formatCurrency((dashboardStats.currentMonthRevenue || 0) / (1 + (dashboardStats.revenueGrowth || 0) / 100))
                      )}
                    </span>
                  </div>
                  <div className={`flex justify-between items-center ${dashboardStats.revenueGrowth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    <span className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium`}>Рост</span>
                    <span className={`${isMobile ? 'text-base' : 'text-lg'} font-semibold flex items-center`}>
                      <TrendingUp className="h-4 w-4 mr-1" />
                      {loading ? (
                        <div className="h-5 bg-muted rounded animate-pulse w-12 inline-block"></div>
                      ) : (
                        `${dashboardStats.revenueGrowth >= 0 ? '+' : ''}${dashboardStats.revenueGrowth.toFixed(1)}%`
                      )}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {selectedCourier && (
        <CourierDetailsDialog
          courier={selectedCourier}
          open={showDetailsDialog}
          onOpenChange={setShowDetailsDialog}
        />
      )}
    </div>
  );
};

export default Dashboard;
